"use client";

import React, { useMemo, useEffect, useRef } from "react";
import Modal from "react-modal";
import { X, Clock, ShieldCheck } from "lucide-react";

export default function FactoringConsentModal({
  isOpen,
  onRequestClose,
  paymentRate = 0,
  paymentType = "",
  feePercent = 5,
  currency = "USD",
  isSubmitting = false,
  onAccept,
  onDecline,
}) {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    // Garante que ao abrir no mobile o usuário veja o TOPO e possa rolar
    requestAnimationFrame(() => {
      try {
        bodyRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch (e) {
        // fallback simples
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
      }
    });
  }, [isOpen]);

  const formatCurrency = useMemo(() => {
    return (amount) => {
      const n = Number(amount || 0);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      }).format(n);
    };
  }, [currency]);

  const normalizedPaymentType = useMemo(() => {
    return String(paymentType || "").trim();
  }, [paymentType]);

  const originalAmount = Number(paymentRate || 0);
  const fee = Number(feePercent || 0) / 100;
  const factoredAmount = Math.max(0, originalAmount * (1 - fee));

  const bodyText = useMemo(() => {
    const a = formatCurrency(originalAmount);
    const b = formatCurrency(factoredAmount);

    return `Of the ${a} you will receive in ${normalizedPaymentType || "Payment Type"}, with factoring you will receive ${b} within 2 hours.\n\nFor factoring to be completed, you must ensure that the vehicle(s) in this load are properly photographed and that the customer's signature is properly collected.\n\nDo you agree to these terms?`;
  }, [formatCurrency, originalAmount, factoredAmount, normalizedPaymentType]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSubmitting ? undefined : onRequestClose}
      contentLabel="Factoring Consent"
      // ✅ Alinha no topo e permite scroll no overlay (mobile-friendly)
      className="fixed inset-0 z-50 p-4 sm:p-6 flex items-start justify-center overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-50"
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
    >
      {/* ✅ Caixa com altura máxima da viewport; body interno scrollável */}
      <div className="bg-[#1c1b19] rounded-xl max-w-2xl w-full border border-zinc-700 shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Early Pay (Factoring)</h2>
              <p className="text-xs text-white/60">
                Fee of {feePercent}% • Advance payment within 2 hours (after approval)
              </p>
            </div>
          </div>

          <button
            onClick={isSubmitting ? undefined : onRequestClose}
            className={`text-gray-400 hover:text-white transition-colors ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={isSubmitting ? "Please wait..." : "Close"}
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* ✅ Body com scroll */}
        <div ref={bodyRef} className="p-5 overflow-y-auto">
          <div className="bg-black/20 border border-zinc-700 rounded-lg p-4 text-sm text-white/90 whitespace-pre-line leading-relaxed">
            {bodyText}
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-zinc-900/30 border border-zinc-700 rounded-lg p-4">
              <div className="text-xs text-white/60 uppercase tracking-wider">Without factoring</div>
              <div className="mt-2 text-xl font-bold text-white">{formatCurrency(originalAmount)}</div>
              <div className="mt-1 text-xs text-white/60">
                Payment in <span className="text-white">{normalizedPaymentType || "—"}</span>
              </div>
            </div>

            <div className="bg-emerald-900/10 border border-emerald-700/40 rounded-lg p-4">
              <div className="text-xs text-emerald-200 uppercase tracking-wider">With factoring</div>
              <div className="mt-2 text-xl font-bold text-emerald-200">{formatCurrency(factoredAmount)}</div>
              <div className="mt-1 text-xs text-emerald-100/80">Payment within 2 hours</div>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <ShieldCheck className="w-5 h-5 text-blue-300 mt-0.5" />
            <div className="text-xs text-white/80 leading-relaxed">
              By confirming, your request will be submitted for review and approved after analysis.
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={isSubmitting ? undefined : onDecline}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg border border-zinc-600 text-white hover:bg-white/5 transition-colors ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              No
            </button>

            <button
              type="button"
              onClick={isSubmitting ? undefined : onAccept}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg bg-emerald-600 text-black font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></span>
                  Saving...
                </>
              ) : (
                "Yes, I agree"
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}